/**
 * Inline playable HTML for Work artifact Files (and leftover live artifacts
 * that have not materialized yet). Replaces the document file card for .html
 * deliverables so a game or app is playable in the thread.
 */
import React, { useCallback, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { filesApi, coworkerApi } from '@/lib/api';
import { openMessageFile } from '@/constants/utils/files';
import { isHtmlCodeFile } from '@/constants/utils/code_file';
import {
  coworkerArtifactAsFile,
  isHtmlArtifact,
} from '@/components/Coworker/coworkerArtifactTypes';
import HtmlPlayablePreview from '@/components/Coworker/HtmlPlayablePreview';
import WorkSitePublish from './WorkSitePublish';

async function loadHtmlFromFile(file) {
  const fileId = file?.id || file?._id;
  if (fileId) {
    try {
      const payload = await filesApi.fetchCodeText(fileId);
      if (payload?.text) return payload.text;
    } catch { /* fall through to the CDN */ }
  }
  const url = file?.cdnPath || file?.url || file?.uri;
  if (url) {
    const response = await fetch(url);
    if (response.ok) return response.text();
  }
  throw new Error('missing-file');
}

async function loadHtmlFromArtifact(taskId, artifact) {
  const downloadUrl = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path);
  if (downloadUrl) {
    try {
      const response = await fetch(downloadUrl);
      if (response.ok) return response.text();
    } catch { /* fall through to the authenticated proxy */ }
  }
  const response = await coworkerApi.fetchArtifactBinary(taskId, artifact.path, { asProxy: true });
  if (!response.ok) throw new Error('Download failed');
  return response.text();
}

function FileHtmlCard({ file, taskId, canPublish, interactionContext }) {
  const loadHtml = useCallback(() => loadHtmlFromFile(file), [file]);
  const expand = useCallback(() => {
    openMessageFile(canPublish && taskId ? { ...file, parameters: { ...file.parameters, workTaskId: taskId } } : canPublish ? file : { ...file, canPublishSite: false });
  }, [file, taskId, canPublish]);

  return (
    <View style={styles.creation}><HtmlPlayablePreview
      name={file.name || file.originalName}
      sourceKey={JSON.stringify([interactionContext?.userId, file.id || file._id || file.cdnPath || file.name])}
      loadHtml={loadHtml}
      onExpand={expand}
      interactionContext={interactionContext && { ...interactionContext, messageId: `${interactionContext.messageId}:file:${file.id || file._id || file.name}`, title: file.name }}
    />{canPublish && <WorkSitePublish taskId={taskId} file={file} />}</View>
  );
}

function ArtifactHtmlCard({ artifact, taskId, canPublish, interactionContext }) {
  const loadHtml = useCallback(
    () => loadHtmlFromArtifact(taskId, artifact),
    [artifact, taskId],
  );
  const expand = useCallback(async () => {
    const url = await coworkerApi.getArtifactDownloadUrl(taskId, artifact.path).catch(() => null);
    const file = { ...coworkerArtifactAsFile(artifact, url || ''), ...(canPublish ? { parameters: { workTaskId: taskId, workArtifactPath: artifact.path } } : { canPublishSite: false }) };
    openMessageFile(file);
  }, [artifact, taskId, canPublish]);

  return (
    <View style={styles.creation}><HtmlPlayablePreview
      name={artifact.name || artifact.path}
      sourceKey={`${interactionContext?.userId || ''}|${taskId}|${artifact.path}|${artifact.size || 0}`}
      loadHtml={loadHtml}
      onExpand={expand}
      interactionContext={interactionContext && { ...interactionContext, messageId: `${interactionContext.messageId}:artifact:${artifact.path}`, title: artifact.name || artifact.path }}
    />{canPublish && <WorkSitePublish taskId={taskId} artifact={artifact} />}</View>
  );
}

export default function CoworkerHtmlPreview({ files, artifacts, taskId, canPublish = false, interactionContext }) {
  const htmlFiles = useMemo(
    () => (Array.isArray(files) ? files.filter((file) => file && isHtmlCodeFile(file)) : []),
    [files],
  );
  const htmlArtifacts = useMemo(
    () => (Array.isArray(artifacts) ? artifacts.filter((artifact) => isHtmlArtifact(artifact)) : []),
    [artifacts],
  );

  if (htmlFiles.length === 0 && htmlArtifacts.length === 0) return null;

  return (
    <View style={styles.container}>
      {htmlFiles.map((file) => (
        <FileHtmlCard key={file.id || file._id || file.cdnPath || file.name} file={file} taskId={taskId} canPublish={canPublish} interactionContext={interactionContext} />
      ))}
      {htmlArtifacts.map((artifact) => (
        <ArtifactHtmlCard
          key={artifact.path || artifact.name}
          artifact={artifact}
          taskId={taskId}
          canPublish={canPublish}
          interactionContext={interactionContext}
        />
      ))}
    </View>
  );
}

const styles = StyleSheet.create({
  creation: { gap: 8 },
  container: {
    gap: 8,
    marginTop: 8,
    maxWidth: 560,
  },
});
